/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {PageTitle} from '../../../core'
import {Link} from 'react-router-dom'

import {
  Audit,
  KTCard,
  KTCardBody,
  KTCardHeader,
  Notes,
  Select,
  Timeline,
  Loading,
  Datepicker,
} from '../../../components'
import {useDispatch, useSelector} from 'react-redux'
import {checkGetId, checkAddCollection, checkAddPayment} from '../../../redux/slices/check.slice'
import {bankCashAccountGetAll} from '../../../redux/slices/lookup.slice'
import {SourceType, KTSVG, detailPageUrl, DetailPage, ExpenseDetailPage} from '../../../helpers'

import * as Yup from 'yup'
import {useFormik} from 'formik'

import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'

import {useMemoizedValidationSchema, useMemoizedFormValues} from '../../../utils/performanceOptimizations'

const selectCheckDetail = (state) => ({
  detail: state.check.detail,
  transfer: state.check.transfer,
  loading: state.check.loading
})

const selectLookupData = (state) => ({
  accountLookupList: state.lookup.accountLookupList
})

function Detail() {
  const [isCollapse, setIsCollapse] = useState(true)

  const {id} = useParams()
  const intl = useIntl()
  const pageUrl = location.pathname.split('/')

  const {detail, transfer, loading} = useSelector(selectCheckDetail)
  const {accountLookupList} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(checkGetId(id))
  }, [dispatch, id, transfer]) 

  const [stepSchema, setStepSchema] = useState()
  
  useEffect(() => {
    if (detail && (detail.checkStateId == 1 || detail.checkStateId == 2 || detail.checkStateId == 6)) {
      dispatch(bankCashAccountGetAll())
    }

    if (detail && detail.checkTypeId == 1) {
      setStepSchema({
        ToAccountId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        PaymentDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      })
    } else if (detail) {
      setStepSchema({
        FromAccountId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        PaymentDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      })
    }
  }, [detail, dispatch, intl])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape(stepSchema), [stepSchema])

  const formValues = useMemoizedFormValues({
    CheckId: Number(id),
    FromAccountId: '',
    ToAccountId: '',
    PaymentDate: new Date(),
    Description: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    if (detail && detail.checkTypeId == 1) {
      dispatch(checkAddCollection(values))
    } else {
      dispatch(checkAddPayment(values))
    }
    resetForm()
  }, [dispatch, detail])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = [
    {
      title: intl.formatMessage({id: 'BC.CHECKS'}),
      path: '/cash/checks',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ]

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.CHECKS.DETAIL'})}
      </PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>
                              {detail.description}
                            </div>

                            <div className='ms-5'></div>
                          </div>

                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                            {detail && detail.bankName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/duotune/finance/fin001.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.bankName}
                                </div>
                              </>
                            )}

                            <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                              <KTSVG
                                path='/images/icons/duotune/finance/fin004.svg'
                                className='svg-icon svg-icon-2 fs-4 me-1'
                              />
                              {detail.checkNo}
                            </div>

                            <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                              <Link
                                to={detailPageUrl(DetailPage.Project, detail && detail.projectId)}
                                className='text-gray-400 text-hover-primary'
                                target='_blank'
                              >
                                <KTSVG
                                  path='/images/icons/nav/Projects.svg'
                                  className='svg-icon svg-icon-2 fs-4 me-1'
                                />
                                {detail && detail.projectName}
                              </Link>
                            </div>
                          </div>
                        </div>

                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'CHECK.GET.TOTALPRICE'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {intl.formatNumber(detail.totalPrice, {
                                style: 'currency',
                                currency: 'try',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                            </div>
                          </div>

                          {detail.usedAmount != null && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'CHECK.GET.USEDAMOUNT'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                {intl.formatNumber(detail.usedAmount, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </div>
                            </div>
                          )}

                          {detail.remainingAmount != null && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'CHECK.GET.REMAININGAMOUNT'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                {intl.formatNumber(detail.remainingAmount, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </div>
                            </div>
                          )}

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'CHECK.GET.CHECKTYPE'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail.checkTypeName}</div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'CHECK.GET.CHECKSTATE'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail.checkStateName}</div>
                          </div>
                          {detail && detail.defermentDate && (
                            <>
                              <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                                <div className='fw-semibold fs-6 text-gray-400'>
                                  {intl.formatMessage({id: 'CHECK.GET.DEFERMENTDATE'})}
                                </div>
                                <div className='fw-bold fs-4'>
                                  {intl.formatDate(new Date(detail && detail.defermentDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>

              <KTCard>
                <KTCardBody className='p-9'>
                  <Tabs className='nav-line-tabs' fill>
                    <Tab eventKey='general' title={intl.formatMessage({id: 'GENERAL'})}>
                      <div className='row p-5'>
                        <div className='col-lg-10 order-2 order-lg-1'>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'CHECK.GET.EDITDATE'})}
                            </label>
                            {detail && detail.editDate && (
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {intl.formatDate(new Date(detail && detail.editDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'CHECK.GET.FROMCOMPANY'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <Link
                                to={detailPageUrl(DetailPage.CompanySupplier, detail.fromCompanyId)}
                                className='text-dark fw-bold d-block fs-6 text-hover-primary'
                                target='_blank'
                              >
                                {detail.fromCompanyName}
                              </Link>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'CHECK.GET.TOCOMPANY'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <Link
                                to={detailPageUrl(DetailPage.CompanySupplier, detail.toCompanyId)}
                                className='text-dark fw-bold d-block fs-6 text-hover-primary'
                                target='_blank'
                              >
                                {detail.toCompanyName}
                              </Link>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'CHECK.GET.SALEINVOICE'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <Link
                                to={detailPageUrl(DetailPage.SaleInvoice, detail.saleInvoiceId)}
                                className='text-dark fw-bold d-block fs-6 text-hover-primary'
                                target='_blank'
                              >
                                {detail.saleInvoiceName}
                              </Link>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'CHECK.GET.SALEINVOICECOMPANYNAME'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <Link
                                to={detailPageUrl(
                                  DetailPage.CompanySupplier,
                                  detail && detail.saleInvoiceCompanyId
                                )}
                                className='text-dark fw-bold d-block fs-6 text-hover-primary'
                                target='_blank'
                              >
                                {detail && detail.saleInvoiceCompanyName}
                              </Link>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'CHECK.GET.EXPENSEINVOICE'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <Link
                                to={detailPageUrl(ExpenseDetailPage[1], detail && detail.expenseInvoiceId)}
                                className='text-dark fw-bold d-block fs-6 text-hover-primary'
                                target='_blank'
                              >
                                {detail && detail.expenseInvoiceName}
                              </Link>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'CHECK.GET.EXPENSEINVOICECOMPANYNAME'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <Link
                                to={detailPageUrl(
                                  DetailPage.CompanySupplier,
                                  detail && detail.expenseInvoiceCompanyId
                                )}
                                className='text-dark fw-bold d-block fs-6 text-hover-primary'
                                target='_blank'
                              >
                                {detail && detail.expenseInvoiceCompanyName}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Tab>

                    <Tab
                      eventKey='expenseInvoices'
                      title={intl.formatMessage({id: 'CHECK.GET.EXPENSEINVOICES'})}
                    >
                      <div className='p-5'>
                        <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                          <thead>
                            <tr className='fw-bold text-muted'>
                              <th>{intl.formatMessage({id: 'CHECK.GET.EXPENSEINVOICE'})}</th>
                              <th>{intl.formatMessage({id: 'CHECK.GET.EXPENSEINVOICECOMPANYNAME'})}</th>
                              <th>{intl.formatMessage({id: 'CHECK.GET.PROJECTNAME'})}</th>
                              <th className='text-end'>{intl.formatMessage({id: 'CHECK.GET.INVOICEAMOUNT'})}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.expenseInvoices && detail.expenseInvoices.length > 0 ? (
                              detail.expenseInvoices.map((inv) => (
                                <tr key={inv.expenseInvoiceId}>
                                  <td>
                                    <Link
                                      to={detailPageUrl(ExpenseDetailPage[1], inv.expenseInvoiceId)}
                                      className='text-dark fw-bold text-hover-primary d-block fs-6'
                                      target='_blank'
                                    >
                                      {inv.expenseInvoiceName}
                                    </Link>
                                  </td>
                                  <td>
                                    <Link
                                      to={detailPageUrl(DetailPage.CompanySupplier, inv.companyId)}
                                      className='text-dark fw-bold text-hover-primary d-block fs-6'
                                      target='_blank'
                                    >
                                      {inv.companyName}
                                    </Link>
                                  </td>
                                  <td className='text-muted fw-semibold'>{inv.projectName}</td>
                                  <td className='text-end fw-bold'>
                                    {intl.formatNumber(inv.amount, {
                                      style: 'currency',
                                      currency: 'try',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 4,
                                    })}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4} className='text-center text-muted py-5'>-</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Tab>
                  </Tabs>
                </KTCardBody>
              </KTCard>
            </>
          )}
        </div>

        <div className='col-xl-3'>
          {detail && (detail && (detail.checkStateId == 1 || detail.checkStateId == 2 || detail.checkStateId == 6)) && (
            <KTCard className='mb-5'>
              <KTCardHeader
                title={intl.formatMessage({
                  id: detail && detail.checkTypeId == 1 ? 'CHECK.GET.ADDCOLLECTION' : 'CHECK.GET.ADDPAYMENT',
                })}
                collapsed={isCollapse}
                onCollapse={(event) => setIsCollapse(event)}
              />

              <KTCardBody className='p-5' collapseble isShow={isCollapse}>
                <form
                  className='form position-relative mb-6'
                  onSubmit={formik.handleSubmit}
                  noValidate
                >
                  <div className='row mb-6'>
                    <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'CHECK.ADDCOLLECTION.TOACCOUNTID'})}
                    </label>
                    {detail && detail.checkTypeId == 1 && (
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={accountLookupList}
                          {...formik.getFieldProps('ToAccountId')}
                          required
                          onChange={(item) =>
                            formik.setFieldValue('ToAccountId', Number(item && item.value))
                          }
                        />
                        {formik.touched.ToAccountId && formik.errors.ToAccountId && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.ToAccountId}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {detail && detail.checkTypeId != 1 && (
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={accountLookupList}
                          {...formik.getFieldProps('FromAccountId')}
                          required
                          onChange={(item) =>
                            formik.setFieldValue('FromAccountId', Number(item && item.value))
                          }
                        />
                        {formik.touched.FromAccountId && formik.errors.FromAccountId && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.FromAccountId}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'CHECK.ADDCOLLECTION.PAYMENTDATE'})}
                    </label>
                    <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('PaymentDate')}
                        onChange={([date]) => formik.setFieldValue('PaymentDate', date)}
                      />
                      {formik.touched.PaymentDate && formik.errors.PaymentDate && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.PaymentDate}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-12 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'CHECK.ADDCOLLECTION.DESCRIPTION'})}
                    </label>
                    <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                      <textarea
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        rows={2}
                        {...formik.getFieldProps('Description')}
                      />
                    </div>
                  </div>

                  <div className='text-center'>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-kt-users-modal-action='submit'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </KTCardBody>
            </KTCard>
          )}

          <Notes SourceId={Number(id)} SourceTypeId={SourceType.Check} />
          <Timeline
            isRefresh={loading}
            SourceId={Number(id)}
            SourceTypeId={SourceType.Check}
            collapse={false}
          />

          {detail && detail.uniqueIdentifier && <Audit Guid={detail && detail.uniqueIdentifier} />}
        </div>
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Detail}
